function text(value) {
  return value == null ? '' : String(value).trim();
}

function lower(value) {
  return text(value).toLowerCase();
}

function nodeIdVariants(value) {
  const id = lower(value);
  if (!id) return [];
  if (id.startsWith('asset:')) {
    const raw = id.slice('asset:'.length);
    return raw ? [id, raw] : [id];
  }
  return [id, `asset:${id}`];
}

function toArray(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    for (const key of ['relationships', 'edges', 'items', 'results', 'data']) {
      if (Array.isArray(data[key])) return data[key];
    }
  }
  return [];
}

function firstPresent(...values) {
  return values.find(value => value !== undefined && value !== null && value !== '');
}

function nodeMap(data) {
  const map = new Map();
  const nodes = Array.isArray(data?.nodes) ? data.nodes : Array.isArray(data?.assets) ? data.assets : [];
  for (const node of nodes) {
    const id = text(firstPresent(node?.assetId, node?.id, node?.nodeId, node?.externalId));
    if (!id) continue;
    const value = {
      id,
      name: text(firstPresent(node?.displayName, node?.name, node?.hostname, node?.label, id)),
      type: text(firstPresent(node?.assetType, node?.type, node?.kind)),
    };
    for (const key of nodeIdVariants(id)) {
      map.set(key, value);
    }
  }
  return map;
}

function labelize(value, fallback = 'Related asset') {
  const raw = text(value);
  if (!raw) return fallback;
  return raw
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase());
}

function endpointNode(edge, side, nodes) {
  const direct = edge?.[side] ?? {};
  const id = text(firstPresent(
    edge?.[`${side}NodeId`],
    edge?.[`${side}AssetId`],
    edge?.[`${side}Id`],
    edge?.[`${side}_node_id`],
    edge?.[`${side}_asset_id`],
    edge?.[`${side}_id`],
    direct?.nodeId,
    direct?.assetId,
    direct?.id
  ));
  const fromMap = nodeIdVariants(id).map(key => nodes.get(key)).find(Boolean) ?? {};
  const name = text(firstPresent(
    edge?.[`${side}DisplayName`],
    edge?.[`${side}Name`],
    edge?.[`${side}Hostname`],
    direct?.displayName,
    direct?.name,
    direct?.hostname,
    direct?.label,
    fromMap.name,
    id
  ));
  const type = text(firstPresent(
    edge?.[`${side}AssetType`],
    edge?.[`${side}Type`],
    direct?.assetType,
    direct?.type,
    direct?.kind,
    fromMap.type
  ));

  return { id, name, type };
}

export function normalizeAssetRelationshipGraph(data) {
  const nodes = nodeMap(data);
  const relationships = toArray(data)
    .map((edge, index) => {
      if (!edge || typeof edge !== 'object') return null;

      const source = endpointNode(edge, 'source', nodes);
      const target = endpointNode(edge, 'target', nodes);
      if (!source.id || !target.id) return null;

      const type = text(firstPresent(edge.relationshipType, edge.type, edge.kind, edge.label));
      const sourceSystem = text(firstPresent(edge.source, edge.sourceSystem, edge.provider, edge.evidenceSource, 'Backend graph'));

      return {
        id: text(firstPresent(edge.id, edge.relationshipId, edge.edgeId, `${source.id}:${type || 'related'}:${target.id}:${index}`)),
        source,
        target,
        type: type || 'related',
        typeLabel: labelize(type, 'Related asset'),
        sourceSystem,
        confidence: firstPresent(edge.confidence, edge.score, null),
        observedAt: firstPresent(edge.observedAt, edge.lastObservedAt, edge.updatedAt, edge.createdAt, null),
        raw: edge,
      };
    })
    .filter(Boolean);

  return {
    relationships,
    relationshipCount: relationships.length,
    nodeCount: nodes.size || new Set(relationships.flatMap(r => [lower(r.source.id), lower(r.target.id)])).size,
  };
}

function assetKeys(asset) {
  return new Set([
    asset?.assetId,
    asset?.id,
    asset?.externalId,
  ].flatMap(nodeIdVariants).filter(Boolean));
}

export function summarizeAssetRelationships(graph, asset) {
  const keys = assetKeys(asset);
  if (!graph || keys.size === 0) {
    return { relationships: [], total: 0, inbound: 0, outbound: 0, byType: [] };
  }

  const rows = graph.relationships
    .map(relationship => {
      const sourceMatch = nodeIdVariants(relationship.source.id).some(key => keys.has(key));
      const targetMatch = nodeIdVariants(relationship.target.id).some(key => keys.has(key));
      if (!sourceMatch && !targetMatch) return null;

      const direction = sourceMatch && targetMatch ? 'self' : sourceMatch ? 'outbound' : 'inbound';
      const related = sourceMatch ? relationship.target : relationship.source;
      return { ...relationship, direction, related };
    })
    .filter(Boolean);

  const typeCounts = rows.reduce((acc, row) => {
    const key = row.typeLabel;
    acc.set(key, (acc.get(key) ?? 0) + 1);
    return acc;
  }, new Map());

  return {
    relationships: rows,
    total: rows.length,
    inbound: rows.filter(row => row.direction === 'inbound').length,
    outbound: rows.filter(row => row.direction === 'outbound').length,
    byType: [...typeCounts.entries()].map(([type, count]) => ({ type, count })).sort((a, b) => b.count - a.count || a.type.localeCompare(b.type)),
  };
}
